import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "../styles/AdminBookings.css";
import { BOOKINGS_API } from "../config/api";
import { showConfirm } from "../utils/customDialog";

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const prevRef = useRef([]);
  const intervalRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Filter & Pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [selectedBooking, setSelectedBooking] = useState(null);

  /* ================= POPUP ================= */
  const showPopup = (msg, type = "info") => {
    const div = document.createElement("div");
    const isError = type === "error";
    const isSuccess = type === "success";
    
    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${isError ? "#ef4444" : isSuccess ? "#10b981" : "#5d1d2b"};
        color: #fff;
        padding: 14px 20px;
        border-radius: 12px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        font-family: inherit;
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        animation: toastFadeIn 0.3s ease;
      ">
        ${isSuccess ? '✔' : isError ? '❌' : '🔔'} ${msg}
      </div>
    `;

    document.body.appendChild(div);
    setTimeout(() => {
      div.style.animation = "toastFadeOut 0.3s ease";
      setTimeout(() => div.remove(), 300);
    }, 3000);
  };

  /* ================= FETCH BOOKINGS ================= */
  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${BOOKINGS_API}?action=get`);
      const data = await res.json();

      if (data.status === "success") {
        const newData = data.data || [];
        const oldData = prevRef.current || [];

        newData.forEach((newItem) => {
          const oldItem = oldData.find(
            (b) => b.id === newItem.id
          );

          if (oldItem && oldItem.status !== newItem.status) {
            const messages = {
              approved: `${newItem.name} APPROVED`,
              rejected: `${newItem.name} REJECTED`,
              completed: `${newItem.name} COMPLETED`,
            };

            showPopup(
              messages[newItem.status] ||
                `${newItem.name} updated`,
              "success"
            );
          }

          if (!oldItem) {
            showPopup(
              `New Booking: ${newItem.name} - ${newItem.service}`,
              "info"
            );
          }
        });

        setBookings(newData);
        prevRef.current = newData;
      }
    } catch (err) {
      console.error("Fetch Bookings Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================= LIVE REFRESH ================= */
  useEffect(() => {
    fetchBookings();

    intervalRef.current = setInterval(
      fetchBookings,
      3000
    );

    return () => clearInterval(intervalRef.current);
  }, [fetchBookings]);



  /* ================= UPDATE STATUS ================= */
  const updateStatus = async (id, status) => {
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("status", status);

      const res = await fetch(
        `${BOOKINGS_API}?action=updateStatus`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await res.json();

      if (result.status === "success") {
        setBookings((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, status }
              : item
          )
        );

        // Keep selectedBooking updated if open in modal
        if (selectedBooking && selectedBooking.id === id) {
          setSelectedBooking((prev) => ({ ...prev, status }));
        }

        showPopup(`${status.toUpperCase()} updated`, "success");
      } else {
        showPopup("Update failed", "error");
      }
    } catch (err) {
      console.error("Status Update Error:", err);
      showPopup("Server error", "error");
    }
  };

  /* ================= DELETE ================= */
  const deleteBooking = async (id) => {
    const ok = await showConfirm({
      title: "Delete Booking",
      message: "Are you sure you want to delete this booking?",
      subMessage: "This will permanently remove the booking record.",
      confirmText: "Delete Booking",
      type: "danger"
    });
    if (!ok) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("id", id);

      const res = await fetch(
        `${BOOKINGS_API}?action=delete`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await res.json();

      if (result.status === "success") {
        setBookings((prev) =>
          prev.filter((item) => item.id !== id)
        );

        if (selectedBooking && selectedBooking.id === id) {
          setSelectedBooking(null);
        }

        showPopup("Deleted successfully", "success");
      } else {
        showPopup("Delete failed", "error");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      showPopup("Server error", "error");
    }
  };

  // Filter Bookings logic
  const filteredBookings = bookings.filter((b) => {
    const matchesSearch =
      b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.phone.includes(searchTerm) ||
      b.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.location && b.location.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" || b.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="admin-container bookings-admin-container">
      {/* ================= HEADER ================= */}
      <div className="bookings-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-calendar-check-fill"></i> Pooja & Service Bookings
          </h1>
          <p>Real-time booking monitor and service management panel</p>
        </div>

        {/* Dashboard Quick Stats */}
        <div className="bookings-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon total"><i className="bi bi-journal-bookmark-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{bookings.length}</span>
              <span className="metric-label">Total Logs</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon pending"><i className="bi bi-hourglass-split"></i></div>
            <div className="metric-info">
              <span className="metric-value">{bookings.filter((b) => b.status === "pending").length}</span>
              <span className="metric-label">Pending</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon approved"><i className="bi bi-patch-check-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{bookings.filter((b) => b.status === "approved").length}</span>
              <span className="metric-label">Approved</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon completed"><i className="bi bi-bookmark-check-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{bookings.filter((b) => b.status === "completed").length}</span>
              <span className="metric-label">Completed</span>
            </div>
          </div>
        </div>
      </div>

      {/* ================= FILTERS & DECK ================= */}
      <div className="bookings-filter-deck">
        <div className="search-box-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="admin-search-input"
            style={{ paddingLeft: "48px" }}
            placeholder="Search by customer, phone, location or service name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="filter-select-wrapper">
          <i className="bi bi-funnel-fill"></i>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Booking Statuses</option>
            <option value="pending">⏳ Pending</option>
            <option value="approved">✔ Approved</option>
            <option value="rejected">❌ Rejected</option>
            <option value="completed">🎯 Completed</option>
          </select>
        </div>
      </div>

      {/* ================= TABLE / CONTENT ================= */}
      {loading ? (
        <div className="bookings-loading-box">
          <div className="spinner"></div>
          <p>Syncing live bookings stream...</p>
        </div>
      ) : (
        <section className="bookings-table-section">
          {filteredBookings.length === 0 ? (
            <div className="bookings-empty-state">
              <i className="bi bi-calendar-x-fill"></i>
              <h3>No Bookings Logged</h3>
              <p>Try refining your search filters. New customer pooja bookings will appear here automatically.</p>
            </div>
          ) : (
            <>
              <div className="bookings-table-wrapper">
                <table className="bookings-admin-table">
                  <thead>
                    <tr>
                      <th>Customer Name</th>
                      <th>Phone</th>
                      <th>Requested Service</th>
                      <th>Scheduled Date</th>
                      <th>Location / Address</th>
                      <th>Status</th>
                      <th style={{ width: "200px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentItems.map((b) => {
                      let displayDate = b.date;
                      try {
                        const parsed = new Date(b.date);
                        if (!isNaN(parsed)) {
                          displayDate = parsed.toLocaleDateString("en-IN", { dateStyle: "medium" });
                        }
                      } catch (e) {}

                      return (
                        <tr key={b.id}>
                          <td className="customer-name-td">
                            <strong>{b.name}</strong>
                          </td>
                          <td>{b.phone}</td>
                          <td className="service-td">{b.service}</td>
                          <td className="date-td">{displayDate}</td>
                          <td className="location-td">{b.location}</td>
                          <td>
                            <span className={`status-pill ${b.status}`}>
                              {b.status === "pending" && <i className="bi bi-hourglass-split"></i>}
                              {b.status === "approved" && <i className="bi bi-check-circle-fill"></i>}
                              {b.status === "rejected" && <i className="bi bi-x-circle-fill"></i>}
                              {b.status === "completed" && <i className="bi bi-bookmark-check-fill"></i>}
                              {b.status}
                            </span>
                          </td>

                          <td>
                            <div className="table-actions-row">
                              <button
                                type="button"
                                className="action-btn-view"
                                onClick={() => setSelectedBooking(b)}
                                title="View Details & Notes"
                              >
                                <i className="bi bi-eye-fill"></i>
                              </button>
                              
                              <select
                                value={b.status}
                                onChange={(e) => updateStatus(b.id, e.target.value)}
                                className="table-status-select"
                              >
                                <option value="pending">Pending</option>
                                <option value="approved">Approve</option>
                                <option value="rejected">Reject</option>
                                <option value="completed">Complete</option>
                              </select>

                              <button
                                type="button"
                                className="action-btn-delete"
                                onClick={() => deleteBooking(b.id)}
                                title="Delete Booking Record"
                              >
                                <i className="bi bi-trash-fill"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ================= PAGINATION ================= */}
              {totalPages > 1 && (
                <div className="bookings-pagination">
                  <button
                    type="button"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="page-nav-btn"
                  >
                    <i className="bi bi-chevron-left"></i> Previous
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => paginate(page)}
                        className={`page-num-btn ${currentPage === page ? "active" : ""}`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="page-nav-btn"
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ================= PORTAL DETAILS MODAL ================= */}
      {selectedBooking &&
        createPortal(
          <div className="admin-portal-root bookings-modal-portal-wrapper">
            <div className="bookings-modal-overlay" onClick={() => setSelectedBooking(null)}>
              <div className="bookings-modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <i className="bi bi-info-circle-fill"></i> Booking Details
                  </h3>
                  <button
                    type="button"
                    className="close-modal-btn"
                    onClick={() => setSelectedBooking(null)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>

                <div className="modal-body">
                  <div className="modal-detail-group">
                    <label>Customer Name</label>
                    <p className="detail-value">{selectedBooking.name}</p>
                  </div>

                  <div className="modal-detail-row">
                    <div className="modal-detail-group">
                      <label>Phone Number</label>
                      <p className="detail-value">{selectedBooking.phone}</p>
                    </div>
                    <div className="modal-detail-group">
                      <label>Requested Service</label>
                      <p className="detail-value highlighted">{selectedBooking.service}</p>
                    </div>
                  </div>

                  <div className="modal-detail-row">
                    <div className="modal-detail-group">
                      <label>Scheduled Date</label>
                      <p className="detail-value">
                        {(() => {
                          try {
                            const parsed = new Date(selectedBooking.date);
                            return !isNaN(parsed) ? parsed.toLocaleDateString("en-IN", { dateStyle: "long" }) : selectedBooking.date;
                          } catch (e) {
                            return selectedBooking.date;
                          }
                        })()}
                      </p>
                    </div>
                    <div className="modal-detail-group">
                      <label>Status</label>
                      <div>
                        <span className={`status-pill ${selectedBooking.status}`}>
                          {selectedBooking.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="modal-detail-group">
                    <label>Location / Address</label>
                    <p className="detail-value">{selectedBooking.location || "No address provided"}</p>
                  </div>

                  <div className="modal-detail-group">
                    <label>User Notes & Special Requests</label>
                    <div className="notes-box">
                      <p>{selectedBooking.notes || "No special requests or notes from customer."}</p>
                    </div>
                  </div>

                  <div className="modal-actions-panel">
                    <div className="status-selector-block">
                      <label>Moderator Action:</label>
                      <select
                        value={selectedBooking.status}
                        onChange={(e) => updateStatus(selectedBooking.id, e.target.value)}
                        className="modal-status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approve</option>
                        <option value="rejected">Reject</option>
                        <option value="completed">Complete</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      className="modal-delete-btn"
                      onClick={() => deleteBooking(selectedBooking.id)}
                    >
                      <i className="bi bi-trash-fill"></i> Delete Record
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default AdminBookings;
